import React, { useMemo } from 'react';

/**
 * Lightweight password strength meter — no zxcvbn dep (would add ~400KB).
 *
 * Heuristic scoring 0-5 based on length + character-class diversity.
 * Deliberately not a perfect crack-time estimator — just enough to nudge
 * agents away from `123456` while staying friendly to a memorable
 * passphrase like `redcarpetsunday`.
 *
 * Renders nothing when the password is empty so the card stays quiet on
 * first paint.
 */
const LEVELS = [
  { label: 'Too short',    tone: 'bg-slate-300 text-slate-500', width: 10 },
  { label: 'Very weak',    tone: 'bg-red-500 text-red-800',      width: 20 },
  { label: 'Weak',         tone: 'bg-orange-500 text-orange-800', width: 40 },
  { label: 'Fair',         tone: 'bg-amber-500 text-amber-800',  width: 60 },
  { label: 'Good',         tone: 'bg-lime-500 text-lime-800',    width: 80 },
  { label: 'Strong',       tone: 'bg-emerald-500 text-emerald-800', width: 100 },
];

function scorePassword(pw) {
  if (!pw) return -1;
  if (pw.length < 6) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(5, score);
}

const PasswordStrengthMeter = ({ password, testId = 'password-strength' }) => {
  const score = useMemo(() => scorePassword(password || ''), [password]);
  if (score < 0) return null;
  const level = LEVELS[score];
  return (
    <div className="mt-1" data-testid={testId}>
      <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
        <div
          className={`h-full transition-all ${level.tone.split(' ')[0]}`}
          style={{ width: `${level.width}%` }}
          data-testid={`${testId}-bar`}
        />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px]">
        <span className="text-slate-500" data-testid={`${testId}-label`}>
          Password strength: <strong className={level.tone.split(' ').slice(1).join(' ')}>{level.label}</strong>
        </span>
        {score < 3 && password.length >= 6 && (
          <span className="text-slate-400" data-testid={`${testId}-tip`}>
            Try mixing upper/lower case, a number, or a symbol
          </span>
        )}
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
